// packages/ui/src/RadioGroup.tsx

import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

// Internal styled Radio primitive
const Radio: React.FC<{
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ id, name, value, checked, onChange, disabled }) => (
  <button
    id={id}
    name={name}
    type="button"
    role="radio"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(value)}
    className={clsx(
      'peer h-5 w-5 shrink-0 rounded-full border-2 border-black transition-all flex items-center justify-center',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
      '[.theme-liquid-glass_&]:border-white/30 [.theme-liquid-glass_&]:bg-white/5',
      '[.theme-liquid-glass_&]:backdrop-blur-sm',
      '[.theme-liquid-glass_&]:focus-visible:ring-violet-400 [.theme-liquid-glass_&]:focus-visible:ring-offset-slate-900',
      'disabled:cursor-not-allowed disabled:opacity-50'
    )}
  >
    <span
      className={clsx(
        'h-2.5 w-2.5 rounded-full transition-colors',
        checked ? 'bg-emerald-500' : 'bg-transparent',
        checked && '[.theme-liquid-glass_&]:bg-violet-400'
      )}
    />
  </button>
);

export interface RadioGroupOption {
  value: string;
  label: string;
  labelKey?: string;
  description?: string;
  descriptionKey?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  label: string;
  labelKey?: string;
  options: RadioGroupOption[];
  value: string | undefined;
  onChange: (newValue: string | undefined) => void;
  name?: string;
  className?: string;
  labelClassName?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  labelKey,
  options,
  value,
  onChange,
  name,
  className,
  labelClassName,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const groupName = name || generatedId;

  const resolvedLabel = labelKey ? t(labelKey) : label;

  return (
    <div className={clsx('space-y-3', className)}>
      <p
        id={`${generatedId}-label`}
        className={clsx(
          'block text-base font-semibold text-slate-800',
          '[.theme-liquid-glass_&]:text-white/90',
          labelClassName
        )}
      >
        {resolvedLabel}
      </p>
      <div role="radiogroup" aria-labelledby={`${generatedId}-label`} className="space-y-2">
        {options.map((option, index) => {
          const optionId = `${generatedId}-option-${index}`;
          const descriptionId = `${optionId}-description`;
          const resolvedOptionLabel = option.labelKey ? t(option.labelKey) : option.label;
          const resolvedDescription = option.descriptionKey ? t(option.descriptionKey) : option.description;

          return (
            <div key={option.value} className="flex items-start gap-3">
              <Radio
                id={optionId}
                name={groupName}
                value={option.value}
                checked={value === option.value}
                onChange={onChange}
                disabled={option.disabled}
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor={optionId}
                  className={clsx(
                    'text-sm font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/90',
                    option.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                  )}
                >
                  {resolvedOptionLabel}
                </label>
                {resolvedDescription && (
                  <p id={descriptionId} className="text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
                    {resolvedDescription}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

RadioGroup.displayName = 'RadioGroup';
